import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Icon, Button, ButtonToolbar, Dropdown } from "rsuite";
import { DatasetProvider, useDataset } from "../DataSet/DatasetProvider";
import DynamicFilters from "../Filters/DynamicFilters";
import DynamicGrid from "../Grid/DynamicGrid";
import { formatNumber } from "../Grid/ValueFormatters";
import "./DynamicScreen.scss";
import DynamicDetailViewer from "./DynamicDetailViewer";
import ExportProgressBar from "./ExportProgressBar";

interface DynamicScreenProps {
  title: string;
  dataSourceId: string;
  beta?: boolean;
}

interface DynamicScreenContentProps {
  title: string;
}

const DynamicScreen = (props: DynamicScreenProps) => {
  return (
    <div className="DynamicScreen">
      <DatasetProvider dataSourceId={props.dataSourceId}>
        <DynamicScreenContent title={props.title} />
      </DatasetProvider>
    </div>
  );
};

const DynamicScreenContent = ({ title }: DynamicScreenContentProps) => {
  const [showVerticalFilters, setShowVerticalFilters] = useState(false);
  const {
    count,
    filteredCount,
    filtersState,
    resetFilters,
    requiredFiltersSatisfied,
    requiredFiltersWarningMessage,
    selectedItemForDetail,
    setSelectedItemForDetail,
    settings,
    exportData,
    cancelExport,
    dismissExportError,
    exportProgress,
    isDefinitionLoaded,
    isLoading,
    refreshData,
  } = useDataset();
  const [msgCount, setMsgCount] = useState("");
  const [msgWarning, setMsgWarning] = useState("");

  const { id } = useParams<{ id?: string }>();

  useEffect(() => {
    if (id && settings?.detailView?.dataSourceId) {
      setSelectedItemForDetail({
        idObject: id,
        dataSourceId: String(settings.detailView.dataSourceId),
        title: settings?.detailView?.title ?? "Details",
      });
    } else if (!id) {
      setSelectedItemForDetail(null);
    }
  }, [
    id,
    setSelectedItemForDetail,
    settings?.detailView?.dataSourceId,
    settings?.detailView?.title,
  ]);

  useEffect(() => {
    if (isLoading) {
      setMsgCount("Loading data...");
    } else if ((count ?? 0) > 0) {
      setMsgCount(
        `Showing ${formatNumber(filteredCount)} of ${formatNumber(count)}`,
      );
    } else {
      setMsgCount("No items to display");
    }

    if (requiredFiltersSatisfied) {
      setMsgWarning("");
    } else {
      setMsgWarning(requiredFiltersWarningMessage);
    }
  }, [
    count,
    filteredCount,
    filtersState,
    requiredFiltersSatisfied,
    requiredFiltersWarningMessage,
    setMsgCount,
    isLoading,
  ]);

  return (
    <>
      <DynamicDetailViewer
        className={selectedItemForDetail ? "block" : "hidden"}
        item={selectedItemForDetail!}
      />
      <div className={selectedItemForDetail ? "hidden" : "block"}>
        <div className="header">
          <div className="header-row header-title-row">
            <h1>{title}</h1>
            {isDefinitionLoaded && (
              <ButtonToolbar className="export-toolbar">
                <Button
                  className="refresh-btn"
                  appearance="ghost"
                  onClick={refreshData}
                  title="Refresh data"
                >
                  <Icon icon="refresh" />
                </Button>
                {settings?.export?.enabled && (
                  <Dropdown
                    title="Export"
                    toggleComponentClass={Button}
                    appearance="primary"
                    placement="bottomEnd"
                  >
                    <Dropdown.Item
                      icon={<Icon icon="file-text-o" />}
                      onClick={() => exportData("csv")}
                    >
                      Export to CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      icon={<Icon icon="file-excel-o" />}
                      onClick={() => exportData("xlsx")}
                    >
                      Export to XLSX
                    </Dropdown.Item>
                  </Dropdown>
                )}
              </ButtonToolbar>
            )}
          </div>
          <div className="header-row header-info-row">
            <span className="msg-count">{msgCount}</span>
            <span className="dot">·</span>
            <span className="clear-filters" onClick={resetFilters} role="button">
              Reset Filters
            </span>
            {msgWarning ? (
              <>
                <span className="dot">·</span>
                <Icon icon="exclamation-circle" />
                <span className="msg-warning">{msgWarning} </span>
              </>
            ) : null}
          </div>
        </div>

        <DynamicFilters
          displayStyle="horizontal"
          onToggleAllFilters={() => setShowVerticalFilters((prev) => !prev)}
        />

        <div className="grid-and-filter-container">
          {showVerticalFilters && (
            <DynamicFilters displayStyle="vertical" showAllFilters />
          )}
          <DynamicGrid />
        </div>
      </div>
      <ExportProgressBar progress={exportProgress} onCancel={cancelExport} onDismissError={dismissExportError} />
    </>
  );
};

export default DynamicScreen;
